import { Sigil, Hallmark, StatusRing, HallmarkBadge } from "@/components/Sigil";
import { Link } from "react-router-dom";
import {
  MarkAperture,
  MarkLigature,
  MarkManuscriptInitial,
  MarkPrivativeAlpha,
  MarkAlphaLambda,
  MarkWitnessAlpha,
} from "@/components/Logo";

const TOKENS = [
  { group: "Surfaces", items: [
    { name: "bg/page", value: "#F8F4EA", desc: "Parchment cream — page background" },
    { name: "bg/elev", value: "#FCFAF4", desc: "Card / table surface" },
    { name: "bg/elev2", value: "#FFFFFF", desc: "Modal / sheet — true white" },
    { name: "bg/revoked", value: "#F5E5DF", desc: "Tinted parchment for revoked" },
    { name: "bg/alert", value: "#F3DED4", desc: "Tinted parchment for denial banner" },
  ]},
  { group: "Lines", items: [
    { name: "line", value: "#CFC7B0", desc: "Card outlines" },
    { name: "line-soft", value: "#E5DDC8", desc: "Hairline rule, table dividers" },
    { name: "line-stamp", value: "#B6AC8E", desc: "Engraved frame" },
  ]},
  { group: "Type", items: [
    { name: "bone", value: "#1A1410", desc: "Warm ink — primary text · never #000" },
    { name: "slate", value: "#5C5346", desc: "Secondary · labels" },
    { name: "slate-2", value: "#76695A", desc: "Tertiary · helper text" },
    { name: "slate-3", value: "#8E8470", desc: "Muted · footnote" },
  ]},
  { group: "Accents", items: [
    { name: "bronze", value: "#9A6B2C", desc: "ATTESTATION ONLY — hallmarks, on-chain" },
    { name: "bronze-deep", value: "#6B4A1C", desc: "Engraved emphasis" },
    { name: "bronze-tint", value: "#F0E4CC", desc: "Hallmark badge background" },
    { name: "oxblood", value: "#7A1F1F", desc: "CONSEQUENCE ONLY — denied, revoked" },
    { name: "oxblood-bright", value: "#9A2F2F", desc: "Hover state for destructive" },
    { name: "oxblood-tint", value: "#F5E5DF", desc: "Denial banner, denied row tint" },
  ]},
];

const TYPE_ROLES = [
  { token: "display/hero", family: "Instrument Serif Italic", size: "60/60", role: "Splash / hero only", sample: "Altheia", italic: true },
  { token: "display/page", family: "Instrument Serif", size: "44/48", role: "Page header agent name", sample: "agent-trader-3" },
  { token: "display/section", family: "Instrument Serif", size: "26/30", role: "Section heads", sample: "Audit log" },
  { token: "display/modal", family: "Instrument Serif", size: "40/42", role: "Modal title — the seal moment", sample: "Break the seal" },
  { token: "body/large", family: "Geist 400", size: "15/22", role: "Hero copy, modal lede", sample: "Trust is the assay; the chronicle is the proof." },
  { token: "body/default", family: "Geist 400", size: "13.5/20", role: "Default body / table content", sample: "The agent will stop signing within one slot of confirmation." },
  { token: "kicker", family: "Geist Mono 600", size: "10/14 · 0.18em", role: "Section kickers, status pips", sample: "COMMITTED ON-CHAIN", uppercase: true },
  { token: "label", family: "Geist 500", size: "11/14 · 0.10em", role: "Buttons, nav (UPPERCASE)", sample: "BREAK THE SEAL", uppercase: true },
  { token: "mono/data", family: "Geist Mono 400", size: "12/18", role: "Telemetry, slot numbers, gas", sample: "284,512,847 · 0.000014 SOL" },
];

export function BrandPage() {
  return (
    <div className="brand-page">
      <header className="brand-page__chrome">
        <Link to="/" className="brand-back">← Altheia</Link>
        <span className="kicker">Brand · Altheia v0.1</span>
        <span className="brand-page__greek">ἀλήθεια — unconcealment</span>
      </header>

      <section className="brand-section">
        <span className="kicker">Logo · 6 directions · etymology pass</span>
        <h1 className="brand-section__title">The mark is the privative</h1>
        <p className="brand-section__lede">
          ἀλήθεια breaks open as <strong>ἀ-</strong> (privative alpha — the negation prefix) + <strong>λήθεια</strong> (lethe / forgetting). The α negates the lethe and produces truth: <em>un-forgetting</em>. The alpha is doing the work, not the spelling. <strong>Cut Theta is dropped — Theta Network owns the Greek capital theta in crypto.</strong> The new pick is built on the alpha-privative — the etymological structure that's been doing the negation for 2500 years and survives the founder-exit test that decorative typography fails.
        </p>

        <div className="brand-marks brand-marks--six">
          <MarkConcept
            tier="A"
            label="01 · Aperture (placeholder)"
            name="Hex iris"
            mark={<MarkAperture size={120} openAmt={1} />}
            preview={<span className="mark-preview"><MarkAperture size={26} openAmt={1} /><span className="mark-preview__word">Altheia</span></span>}
            right="Two-state animation potential · matches existing Hallmark glyph"
            wrong="Carries hex baggage from Compound / BNB / Helium / Polygon · reads 'crypto default'"
            note="Currently in the topbar. Clever mechanic, generic shape. Hex baggage is real."
          />
          <MarkConcept
            tier="S"
            label="02 · Manuscript Initial"
            name="The illuminated Α"
            mark={<MarkManuscriptInitial size={120} />}
            preview={<span className="mark-preview"><MarkManuscriptInitial size={26} /><span className="mark-preview__word">Altheia</span></span>}
            right="Greek thesis-aligned · scholarly / archival product · zero competitive overlap"
            wrong="Modern dev-tools brand · API-first developer infra"
            note="Nobody in crypto looks like this. Drop-cap-in-frame is publishing convention (Penguin, Folio Society) but unmined in this category. Earns a moat."
          />
          <MarkConcept
            tier="A"
            label="03 · The Ligature"
            name="Wordmark-led (th)"
            mark={<MarkLigature size={120} />}
            preview={<span className="mark-preview"><MarkLigature size={26} /><span className="mark-preview__word">Altheia</span></span>}
            right="Long-game brands (Stripe / Mercury / Mirror pattern) · ages on type personality"
            wrong="Brands that need a strong solo glyph · brands without a serif typographic identity"
            note="Pretty typography, but 'th' has no etymology — just where the syllables land in English. Fails the founder-exit test (no story underneath)."
          />
          <MarkConcept
            tier="S"
            label="04 · The Privative Α"
            name="The bar that does the work"
            pick
            mark={<MarkPrivativeAlpha size={120} />}
            preview={<span className="mark-preview"><MarkPrivativeAlpha size={26} /><span className="mark-preview__word">Altheia</span></span>}
            right="Anchored in 2500 years of Greek grammar · brand story writes itself · zero crypto overlap"
            wrong="Brands that need a multi-symbol mark · brands without classical/serious positioning"
            note="A single capital Α with the crossbar emphasized as the brand device. The crossbar IS the privative — the bar that turns λήθη into ἀλήθεια. Modernist, etymology-led. The story aged 2500 years; it'll age another five."
          />
          <MarkConcept
            tier="S"
            label="05 · Αλ Bound"
            name="The fused ligature"
            mark={<MarkAlphaLambda size={120} />}
            preview={<span className="mark-preview"><MarkAlphaLambda size={26} /><span className="mark-preview__word">Altheia</span></span>}
            right="Etymology made legible — the privative α and the λ of lethe in one glyph"
            wrong="Anyone who needs a single-letter favicon · audiences with zero Greek literacy"
            note="Capital Α and lowercase λ fused into one ligature: the alpha contains and negates the lambda. Two letters, one glyph, full etymology in the form."
          />
          <MarkConcept
            tier="A"
            label="06 · The Witness Α"
            name="Privative + ledger rule"
            mark={<MarkWitnessAlpha size={100} />}
            preview={<span className="mark-preview"><MarkWitnessAlpha size={20} /><span className="mark-preview__word">Altheia</span></span>}
            right="Editorial layouts · masthead use · documentation pages · audit-themed surfaces"
            wrong="Twitter avatar · square brand sockets · favicon (long aspect breaks)"
            note="The Α with its crossbar extending out as a witness signature line. Two metaphors collapsed into one form. Brilliant for editorial, breaks at small square sockets."
          />
        </div>

        <div className="wordmark-block">
          <span className="kicker">Wordmark · primary — Cormorant Garamond + Instrument Serif italic</span>
          <h1 className="wordmark-display">Altheia</h1>
        </div>
      </section>

      <section className="brand-section">
        <span className="kicker">Color · light is the assay</span>
        <h1 className="brand-section__title">Two channels, no candy</h1>
        <p className="brand-section__lede">
          Trust is communicated by typography and weight. Color is reserved for two semantic channels: <strong>bronze for attestation</strong> (this is committed on-chain), <strong>oxblood for consequence</strong> (this is denied / revoked / broken). No green for "good." No yellow for "warning."
        </p>

        {TOKENS.map((g) => (
          <div key={g.group}>
            <span className="kicker brand-token-group">{g.group}</span>
            <div className="token-grid">
              {g.items.map((t) => (
                <div key={t.name} className="token-card">
                  <div className="token-swatch" style={{ background: t.value, borderColor: t.value === "#FFFFFF" || t.value === "#F8F4EA" ? "var(--line)" : "transparent" }} />
                  <div className="token-meta">
                    <code className="token-name">{t.name}</code>
                    <span className="token-value num">{t.value}</span>
                    <span className="token-desc">{t.desc}</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        ))}
      </section>

      <section className="brand-section">
        <span className="kicker">Type · three voices, all free</span>
        <h1 className="brand-section__title">Editorial · ledger · chrome</h1>
        <p className="brand-section__lede">
          <strong>Instrument Serif</strong> for editorial weight — italic for the wordmark and modal titles, upright for section heads. <strong>Geist</strong> as the chrome workhorse with personality enough to escape the Inter trap. <strong>Geist Mono</strong> for every hash, slot, address, amount.
        </p>

        <table className="type-table">
          <thead>
            <tr>
              <th>Token</th>
              <th>Family</th>
              <th>Size / Line</th>
              <th>Role</th>
              <th>Sample</th>
            </tr>
          </thead>
          <tbody>
            {TYPE_ROLES.map((r) => (
              <tr key={r.token}>
                <td><code>{r.token}</code></td>
                <td>{r.family}</td>
                <td className="num">{r.size}</td>
                <td>{r.role}</td>
                <td className={`type-sample ${r.italic ? "type-sample--italic" : ""} ${r.uppercase ? "type-sample--uppercase" : ""}`}>{r.sample}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </section>

      <section className="brand-section">
        <span className="kicker">Components · the kit</span>
        <h1 className="brand-section__title">Drop-in primitives</h1>

        <div className="components-grid">
          <ComponentCard label="Status ring · active">
            <StatusRing status="active" size={20} />
          </ComponentCard>
          <ComponentCard label="Status ring · paused">
            <StatusRing status="paused" size={20} />
          </ComponentCard>
          <ComponentCard label="Status ring · revoked">
            <StatusRing status="revoked" size={20} />
          </ComponentCard>
          <ComponentCard label="Hallmark · bronze">
            <Hallmark size={18} tone="bronze" />
          </ComponentCard>
          <ComponentCard label="Hallmark · oxblood">
            <Hallmark size={18} tone="oxblood" />
          </ComponentCard>
          <ComponentCard label="Hallmark badge">
            <HallmarkBadge>Inscribed</HallmarkBadge>
          </ComponentCard>
        </div>

        <div className="sigil-strip">
          <span className="kicker">Procedural sigils · 6 examples</span>
          <div className="sigil-strip__grid">
            {["agent-trader-3", "agent-mm-1", "agent-yield-2", "agent-research-7", "agent-treasury-1", "agent-keeper-4"].map((seed) => (
              <div key={seed} className="sigil-strip__item">
                <Sigil seed={seed} size={88} tone="bone" sweeping strokeWidth={1.1} />
                <code className="sigil-strip__seed">{seed}</code>
              </div>
            ))}
          </div>
          <p className="brand-section__lede">
            Deterministic — same seed always produces the same sigil. Heraldic vocabulary: outer ring + cardinal ticks + inner ring + wedges + central glyph. Six interior glyph variants chosen by hash.
          </p>
        </div>
      </section>

      <footer className="brand-footer">
        <span className="kicker">Altheia · v0.1</span>
        <em className="brand-footer__greek">ἀλήθεια — unconcealment</em>
        <span className="brand-footer__note">Hand-off bundle: Brand v2 + Agent Detail v2 + Landing</span>
      </footer>

      <BrandPageStyles />
    </div>
  );
}

function MarkConcept({
  label,
  name,
  note,
  mark,
  preview,
  tier,
  pick,
  right,
  wrong,
}: {
  label: string;
  name: string;
  note: string;
  mark: React.ReactNode;
  preview?: React.ReactNode;
  tier?: "S" | "A" | "B";
  pick?: boolean;
  right?: string;
  wrong?: string;
}) {
  return (
    <div className={`mark-concept ${pick ? "mark-concept--pick" : ""}`}>
      <div className="mark-concept__head">
        <span className="kicker">{label}</span>
        {tier && <span className={`mark-tier mark-tier--${tier.toLowerCase()}`}>tier {tier}</span>}
        {pick && <span className="mark-pick">↓ ship this</span>}
      </div>
      <div className="mark-concept__frame">{mark}</div>
      <h3 className="mark-concept__name">{name}</h3>
      {preview && <div className="mark-concept__preview">{preview}</div>}
      <p className="mark-concept__note">{note}</p>
      {(right || wrong) && (
        <div className="mark-concept__rw">
          {right && <div><span className="rw-tag rw-tag--right">RIGHT FOR</span> {right}</div>}
          {wrong && <div><span className="rw-tag rw-tag--wrong">WRONG FOR</span> {wrong}</div>}
        </div>
      )}
    </div>
  );
}

function MarkOculusSmall() {
  return (
    <svg width="26" height="26" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="44" fill="none" stroke="var(--bone)" strokeWidth="3" />
      <circle cx="50" cy="50" r="30" fill="none" stroke="var(--bone)" strokeWidth="2" />
      <circle cx="50" cy="50" r="6" fill="var(--bronze)" />
    </svg>
  );
}

function MarkOculus() {
  return (
    <svg width="120" height="120" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="44" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <circle cx="50" cy="50" r="30" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <line x1="50" y1="6" x2="50" y2="14" stroke="var(--bone)" strokeWidth="1" />
      <line x1="50" y1="86" x2="50" y2="94" stroke="var(--bone)" strokeWidth="1" />
      <line x1="6" y1="50" x2="14" y2="50" stroke="var(--bone)" strokeWidth="1" />
      <line x1="86" y1="50" x2="94" y2="50" stroke="var(--bone)" strokeWidth="1" />
      <circle cx="50" cy="50" r="6" fill="var(--bronze)" />
    </svg>
  );
}

function MarkChronicle() {
  return (
    <svg width="120" height="120" viewBox="0 0 100 100">
      <rect x="14" y="20" width="72" height="60" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <line x1="50" y1="20" x2="50" y2="80" stroke="var(--bone)" strokeWidth="0.8" opacity="0.6" />
      <line x1="22" y1="32" x2="46" y2="32" stroke="var(--bone)" strokeWidth="0.6" />
      <line x1="22" y1="42" x2="46" y2="42" stroke="var(--bone)" strokeWidth="0.6" />
      <line x1="22" y1="52" x2="46" y2="52" stroke="var(--bone)" strokeWidth="0.6" />
      <line x1="54" y1="32" x2="78" y2="32" stroke="var(--bone)" strokeWidth="0.6" />
      <line x1="54" y1="42" x2="78" y2="42" stroke="var(--bone)" strokeWidth="0.6" />
      <line x1="54" y1="52" x2="78" y2="52" stroke="var(--bone)" strokeWidth="0.6" />
      <circle cx="50" cy="68" r="4" fill="var(--bronze)" />
    </svg>
  );
}

function MarkPanopticon() {
  return (
    <svg width="120" height="120" viewBox="0 0 100 100">
      <circle cx="50" cy="50" r="36" fill="none" stroke="var(--bone)" strokeWidth="1" />
      <circle cx="50" cy="50" r="14" fill="none" stroke="var(--bone)" strokeWidth="1" />
      {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => {
        const a = ((deg - 90) * Math.PI) / 180;
        return (
          <line
            key={deg}
            x1={50 + Math.cos(a) * 14}
            y1={50 + Math.sin(a) * 14}
            x2={50 + Math.cos(a) * 36}
            y2={50 + Math.sin(a) * 36}
            stroke="var(--bone)"
            strokeWidth="0.6"
            opacity="0.7"
          />
        );
      })}
      <circle cx="50" cy="50" r="3" fill="var(--bronze)" />
    </svg>
  );
}

function ComponentCard({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="component-card">
      <div className="component-card__demo">{children}</div>
      <span className="kicker">{label}</span>
    </div>
  );
}

function BrandPageStyles() {
  return (
    <style>{`
      .brand-page { max-width: 1200px; margin: 0 auto; padding: var(--s-7) var(--s-6); display: flex; flex-direction: column; gap: var(--s-9); }
      .brand-page__chrome { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; padding-bottom: var(--s-5); border-bottom: 1px solid var(--line-soft); margin-bottom: var(--s-5); }
      .brand-back { color: var(--slate); font: var(--type-mono-data); }
      .brand-back:hover { color: var(--bone); }
      .brand-page__greek { font: var(--type-display-section); font-style: italic; color: var(--bone); text-align: right; }
      .brand-section__title { font: var(--type-display-page); font-style: italic; margin: var(--s-2) 0 var(--s-3); }
      .brand-section__lede { color: var(--slate); font: var(--type-body-large); max-width: 720px; margin: 0 0 var(--s-7); }
      .brand-section__lede strong { color: var(--bone); }
      .brand-marks { display: grid; grid-template-columns: repeat(3, 1fr); gap: var(--s-5); margin-bottom: var(--s-7); }
      .brand-marks--six { grid-template-columns: repeat(3, 1fr); }
      .mark-concept { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-5); display: flex; flex-direction: column; align-items: stretch; gap: var(--s-2); position: relative; }
      .mark-concept--pick { border-color: var(--bronze); box-shadow: 0 0 0 1px var(--bronze); }
      .mark-concept__head { display: flex; align-items: center; gap: var(--s-3); justify-content: space-between; }
      .mark-tier { font-family: var(--font-mono); font-size: 9.5px; letter-spacing: 0.18em; text-transform: uppercase; padding: 2px 6px; border: 1px solid var(--line); }
      .mark-tier--s { border-color: var(--bronze); color: var(--bronze-deep); background: var(--bronze-tint); }
      .mark-tier--a { border-color: var(--line); color: var(--slate); }
      .mark-tier--b { border-color: var(--line-soft); color: var(--slate-3); }
      .mark-pick { font-family: var(--font-mono); font-size: 10px; letter-spacing: 0.18em; text-transform: uppercase; color: var(--bronze-deep); }
      .mark-concept__frame { padding: var(--s-5) 0; align-self: center; }
      .mark-concept__name { font: var(--type-display-section); font-style: italic; margin: var(--s-1) 0; text-align: center; }
      .mark-concept__preview { display: flex; justify-content: center; padding: var(--s-3) var(--s-3); background: var(--bg); border: 1px solid var(--line-soft); margin: var(--s-2) 0; }
      .mark-preview { display: inline-flex; align-items: center; gap: 10px; }
      .mark-preview__word { font-family: 'Cormorant Garamond', 'Instrument Serif', serif; font-size: 24px; color: var(--bone); letter-spacing: -0.005em; line-height: 1; }
      .mark-concept__note { color: var(--slate); font: var(--type-body-small); text-align: left; margin: 0; }
      .mark-concept__rw { display: flex; flex-direction: column; gap: 6px; padding-top: var(--s-2); margin-top: var(--s-2); border-top: 1px dashed var(--line-soft); font: var(--type-body-small); color: var(--slate-2); line-height: 1.5; }
      .rw-tag { display: inline-block; font-family: var(--font-mono); font-size: 9px; letter-spacing: 0.18em; text-transform: uppercase; padding: 1px 5px; margin-right: 6px; border: 1px solid var(--line); }
      .rw-tag--right { color: var(--bronze-deep); border-color: var(--bronze); }
      .rw-tag--wrong { color: var(--oxblood); border-color: var(--oxblood); }
      .wordmark-block { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-7); display: flex; flex-direction: column; gap: var(--s-3); align-items: center; }
      .wordmark-display { font: var(--type-display-hero); font-style: italic; margin: 0; }
      .brand-token-group { display: block; margin: var(--s-5) 0 var(--s-3); }
      .token-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: var(--s-3); margin-bottom: var(--s-4); }
      .token-card { display: grid; grid-template-columns: 56px 1fr; gap: var(--s-3); padding: var(--s-3); background: var(--bg-elev); border: 1px solid var(--line-soft); }
      .token-swatch { width: 56px; height: 56px; border: 1px solid transparent; }
      .token-meta { display: flex; flex-direction: column; gap: 2px; }
      .token-name { font: var(--type-mono-data); font-size: 12px; color: var(--bone); }
      .token-value { color: var(--bronze); font: var(--type-mono-address); }
      .token-desc { color: var(--slate); font: var(--type-body-small); }
      .type-table { width: 100%; border-collapse: collapse; }
      .type-table th, .type-table td { padding: var(--s-3) var(--s-3); text-align: left; border-bottom: 1px solid var(--line-soft); }
      .type-table th { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--slate-2); }
      .type-table td { font: var(--type-body-small); color: var(--slate); }
      .type-table td code { font: var(--type-mono-data); color: var(--bone); }
      .type-sample { color: var(--bone); font: var(--type-body-large); }
      .type-sample--italic { font: var(--type-display-section); font-style: italic; font-size: 24px; }
      .type-sample--uppercase { font: var(--type-kicker); letter-spacing: var(--tracking-kicker); text-transform: uppercase; color: var(--bronze); }
      .components-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(160px, 1fr)); gap: var(--s-3); margin-bottom: var(--s-7); }
      .component-card { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-4); text-align: center; display: flex; flex-direction: column; gap: var(--s-3); align-items: center; }
      .component-card__demo { padding: var(--s-3); display: flex; align-items: center; justify-content: center; min-height: 50px; }
      .sigil-strip { background: var(--bg-elev); border: 1px solid var(--line-soft); padding: var(--s-5); }
      .sigil-strip__grid { display: grid; grid-template-columns: repeat(6, 1fr); gap: var(--s-3); margin: var(--s-4) 0; }
      .sigil-strip__item { display: flex; flex-direction: column; gap: var(--s-2); align-items: center; padding: var(--s-3); }
      .sigil-strip__seed { font: var(--type-mono-address); color: var(--slate); }
      .brand-footer { display: grid; grid-template-columns: 1fr auto 1fr; align-items: center; padding: var(--s-5) 0; border-top: 1px solid var(--line-soft); }
      .brand-footer__greek { font: var(--type-display-section); font-style: italic; text-align: center; }
      .brand-footer__note { color: var(--slate); font: var(--type-mono-address); text-align: right; }
      @media (max-width: 900px) {
        .brand-marks { grid-template-columns: 1fr; }
        .sigil-strip__grid { grid-template-columns: repeat(3, 1fr); }
        .brand-page__chrome { grid-template-columns: 1fr; gap: var(--s-2); text-align: center; }
        .brand-page__greek { text-align: center; }
        .brand-footer { grid-template-columns: 1fr; gap: var(--s-2); text-align: center; }
        .brand-footer__note { text-align: center; }
      }
    `}</style>
  );
}
